import { Container, IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../common/GameFont";
import { BasePageView } from "../../../../common/components/BasePageView";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { UsdShopItemContainer } from "../../components/UsdShopItemContainer";
import { IShopController } from "../../controller/IShopController";
import { IShopModel } from "../../model/IShopModel";
import { ShopItem } from "../../model/ShopItem";
import { ShopResourcePackage } from "../../resources/ShopResourcePackage";
import { DefaultButton } from "../../../../common/components/DefaultButton";
import { ILogger } from "../../../../../logger/ILogger";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";
import { gsap } from "gsap";
import { PaymentResult } from "../../../../../../web3/PaymentResult";
import { Web3Model } from "../../../../../../web3/models/Web3Model";
import { CancellationToken } from "async/CancellationToken";
import { FullscreenInputBlocker } from "../../../../game/components/FullscreenInputBlocker";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { ShopItemCurrencyType } from "../../model/ShopItemCurrencyType";
import { MonShopItemContainer } from "../../components/MonShopItemContainer";
import { UIScrollBox } from "../../../../common/components/UIScrollBox";

export class ShopPage extends BasePageView {
    private static readonly ITEMS_SPACING_X = 40;
    private static readonly ITEMS_SPACING_Y = 37;
    private static readonly HEADER_TEXT = 'SHOP';
    private static readonly PAYMENT_TEXT = '*The prices above may incur additional payment charges';

    private readonly operationControls: PromiseCompletionSource<void>;
    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly shopResources: ShopResourcePackage;
    private readonly shopModel: IShopModel;
    private readonly shopController: IShopController;
    private readonly logger: ILogger;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly web3Model: Web3Model;
    private readonly analyticsProvider: IAnalyticProviderWrapper;

    private viewBackground: Sprite;
    private backButton: DefaultButton;
    private headerText: Text;
    private monItemsContainer: Container;
    private usdItemsContainer: Container;
    private showViewAnimationTimeLine: gsap.core.Timeline;
    private paymentText: Text;
    private scrollBox: UIScrollBox;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        cancellationToken: CancellationToken,
        shopResources: ShopResourcePackage,
        commonResources: CommonResourcesPackage,
        canvasScaler: ICanvasScaler,
        shopModel: IShopModel,
        shopController: IShopController,
        logger: ILogger,
        asyncOperationNotification: AsyncOperationNotification,
        web3Model: Web3Model,
        analyticsProvider: IAnalyticProviderWrapper
    ) {
        super(canvasScaler);
        this.operationControls = operationControls;
        this.cancellationToken = cancellationToken;
        this.shopResources = shopResources;
        this.commonResources = commonResources;
        this.shopModel = shopModel;
        this.shopController = shopController;
        this.logger = logger;
        this.asyncOperationNotification = asyncOperationNotification;
        this.web3Model = web3Model;
        this.analyticsProvider = analyticsProvider;
        this.showViewAnimationTimeLine = new gsap.core.Timeline({ paused: true });
        this.showViewAnimationTimeLine.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        this.build();
        this.start();
    }

    public start(): void {
        this.showView();
    }

    protected override build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.addChild(this.viewBackground);
        this.backButton = new DefaultButton(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width, -this.backButton.height, this.backButton.width * 3, this.backButton.height * 3);
        this.backButton.onUp.add(this.backButtonClickHandle, this);
        this.addChild(this.backButton);
        this.headerText = new Text(ShopPage.HEADER_TEXT, new HeaderTextStyle());
        this.addChild(this.headerText);
        this.paymentText = new Text(ShopPage.PAYMENT_TEXT, new PaymentInfoTextStyle());
        this.addChild(this.paymentText);

        const scrollBoxSizeY = this.canvasScaler.referenceHeight - 470;
        this.scrollBox = new UIScrollBox({
            width: this.canvasScaler.referenceWidth,
            height: scrollBoxSizeY,
            type: 'vertical',
            vertPadding: 30,
            horPadding: 73,
            elementsMargin: 52
        });

        this.scrollBox.onScroll.add(this.InteractivePackages, this);

        this.createMonItems();
        this.createUsdItems();
        if (this.monItemsContainer != null) {
            this.scrollBox.addItem(this.monItemsContainer);
        }
        this.scrollBox.addItem(this.usdItemsContainer);
        this.addChild(this.scrollBox);

        this.scrollBox.setScrollable(this.scrollBox.elementsHeight > scrollBoxSizeY);
        this.scrollBox.setWheelScrollable(this.scrollBox.elementsHeight > scrollBoxSizeY);

        super.build();
    }

    private createUsdItems(): void {
        this.usdItemsContainer = new Container();
        const usdShopItems = this.shopModel.shopItems.value.filter((item) => item.currency === ShopItemCurrencyType.USD);
        usdShopItems.forEach((usdShopItem, index) => {
            const usdShopItemContainer = new UsdShopItemContainer(this.shopResources, usdShopItem);
            usdShopItemContainer.eventMode = "static";
            usdShopItemContainer.position.set(index % 2 * (usdShopItemContainer.width + ShopPage.ITEMS_SPACING_X), Math.floor(index / 2) * (usdShopItemContainer.height + ShopPage.ITEMS_SPACING_Y));
            usdShopItemContainer.onUsdPackageClick.add(() => this.onUsdItemClick(usdShopItem));
            this.usdItemsContainer.addChild(usdShopItemContainer);
        });
    }

    private createMonItems(): void {
        const monShopItems = this.shopModel.shopItems.value.filter((item) => item.currency === ShopItemCurrencyType.MON);
        if (monShopItems.length === 0) {
            return;
        }
        this.monItemsContainer = new Container();
        monShopItems.forEach((monShopItem, index) => {
            const monShopItemContainer = new MonShopItemContainer(this.shopResources, monShopItem);
            monShopItemContainer.eventMode = "static";
            monShopItemContainer.position.set(0, index * (monShopItemContainer.height + ShopPage.ITEMS_SPACING_Y));
            monShopItemContainer.onMonPackageClick.add(() => this.onMonItemClick(monShopItem));
            this.monItemsContainer.addChild(monShopItemContainer);
        });
    }

    private InteractivePackages(): void {
        this.usdItemsContainer.children.forEach(item => item.eventMode = "static");
        if (this.monItemsContainer != null) {
            this.monItemsContainer.children.forEach(item => item.eventMode = "static");
        }
    }

    private async showView(): Promise<void> {
        await this.showViewAnimationTimeLine.play();
    }

    private async hideView(): Promise<void> {
        this.showViewAnimationTimeLine.reverse();
    }

    private async backButtonClickHandle(): Promise<void> {
        this.backButton.eventMode = "none";
        await this.hideView();
        this.operationControls.complete();
    }

    private async onUsdItemClick(shopItem: ShopItem): Promise<void> {
        const fullscreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        try {
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Click on store package", this.analyticsProvider.currentEventIndex));
            this.asyncOperationNotification.show();
            const paymentResult = await this.shopController.showPaymentPopup(shopItem.tokenId, shopItem.price);
            this.logger.debug("Payment finished with result: " + paymentResult);
            if (paymentResult === PaymentResult.SUCCESS) {
                this.asyncOperationNotification.hide();
                await this.shopController.showMyWalletPopup();
            }
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            this.asyncOperationNotification.hide();
            fullscreenInputBlocker.destroy();
        }
    }

    private async onMonItemClick(shopItem: ShopItem): Promise<void> {
        const fullscreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        try {
            const paymentResult = await this.shopController.showMonPaymentProcessingPopup(shopItem.price, shopItem.tokenId);
            this.logger.debug("Payment finished with result: " + paymentResult);
            if (paymentResult === PaymentResult.SUCCESS) {
                this.asyncOperationNotification.hide();
                await this.shopController.showMyWalletPopup();
            }
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            this.asyncOperationNotification.hide();
            fullscreenInputBlocker.destroy();
        }
    }

    protected override layout(width: number, height: number): void {
        this.backButton.position.set(55, 235);
        this.headerText.position.set(145, 239);
        this.scrollBox.position.set(0, 338);
        this.paymentText.position.set(width * 0.5 - this.paymentText.width * 0.5, 1690);
        super.layout(width, height);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.usdItemsContainer.children?.forEach((usdItemContainer) => (usdItemContainer as UsdShopItemContainer).onUsdPackageClick.removeAll());
        if (this.monItemsContainer != null) {
            this.monItemsContainer.children?.forEach((monItemContainer) => (monItemContainer as MonShopItemContainer).onMonPackageClick.removeAll());
        }
        this.scrollBox.onScroll.remove(this.InteractivePackages, this);
        this.backButton.onUp.remove(this.backButtonClickHandle, this);
        this.showViewAnimationTimeLine?.kill();
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 60
        }));
    }
}

class PaymentInfoTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            letterSpacing: 1.25
        });
    }
}
